import { ReactNode } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface KiiDialogLayoutProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl";
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl";
  hasUnsavedChanges?: boolean;
}

const maxWidthClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
};

/**
 * KiiDialogLayout - Self-contained dialog layout component with page-level scrolling
 *
 * A standardized dialog wrapper with:
 * - Consistent header structure
 * - Configurable width (7 sizes)
 * - Page-level scrolling (scroll the entire overlay, not internal content)
 * - Unsaved changes protection
 * - Proper accessibility
 */
export function KiiDialogLayout({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  maxWidth,
  size,
  hasUnsavedChanges = false,
}: KiiDialogLayoutProps) {
  const dialogSize = size || maxWidth || "lg";

  const handleOpenChange = (newOpen: boolean) => {
    if (hasUnsavedChanges && !newOpen) {
      return;
    }
    onOpenChange(newOpen);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/80 overflow-y-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        >
          <div className="min-h-full flex items-start justify-center py-8 px-4">
            <DialogPrimitive.Content
              className={cn(
                "relative w-full border bg-background p-6 shadow-lg sm:rounded-lg",
                "data-[state=open]:animate-in data-[state=closed]:animate-out",
                "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
                "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
                maxWidthClasses[dialogSize]
              )}
              onPointerDownOutside={(e) => {
                if (hasUnsavedChanges) {
                  e.preventDefault();
                }
              }}
              onEscapeKeyDown={(e) => {
                if (hasUnsavedChanges) {
                  e.preventDefault();
                }
              }}
            >
              <div className="flex flex-col space-y-1.5 text-center sm:text-left">
                <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                  {title}
                </DialogPrimitive.Title>
                {description && (
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {description}
                  </DialogPrimitive.Description>
                )}
              </div>
              {children}
              {footer && (
                <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
                  {footer}
                </div>
              )}
              <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            </DialogPrimitive.Content>
          </div>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
